/**
 * The colour language of the payouts screen.
 *
 * Every stage money passes through has one colour, used the same way on the
 * tiles, the lifecycle strip, the action cards and the status pills, so an
 * operator can read where money is by colour before reading a label:
 *
 *   sky      money in — the customer has paid
 *   amber    waiting on time — inside the return window
 *   orange   waiting on someone else — cash still with the courier
 *   violet   ready — the next run will credit it
 *   emerald  done — credited to the seller
 *   teal     done — in the seller's bank
 *   indigo   the platform's own share (commission)
 *   rose     money going back, or something that failed
 *
 * The brand's shared "success" badge is deliberately deep gold, not green. A
 * finance screen needs "settled" and "waiting" to look different at a glance,
 * so it uses this palette instead of changing that token for every app.
 *
 * Class strings are written out in full so Tailwind can find them.
 */
export type StageTone =
  | 'sky'
  | 'amber'
  | 'orange'
  | 'violet'
  | 'emerald'
  | 'teal'
  | 'indigo'
  | 'rose'
  | 'slate';

interface ToneClasses {
  /** Tinted card surface and border. */
  card: string;
  /** Solid accent: a bar, a dot, a step marker. */
  solid: string;
  /** Icon chip background and colour. */
  chip: string;
  /** The amount — the darkest shade, so it passes contrast on the tint. */
  amount: string;
  /** Uppercase label on the tint. */
  label: string;
  /** Status pill. */
  pill: string;
}

export const TONES: Record<StageTone, ToneClasses> = {
  sky: {
    card: 'border-sky-200/80 bg-sky-50/70',
    solid: 'bg-sky-500',
    chip: 'bg-sky-100 text-sky-700',
    amount: 'text-sky-900',
    label: 'text-sky-800',
    pill: 'bg-sky-50 text-sky-800 ring-sky-200',
  },
  amber: {
    card: 'border-amber-200/80 bg-amber-50/70',
    solid: 'bg-amber-500',
    chip: 'bg-amber-100 text-amber-700',
    amount: 'text-amber-900',
    label: 'text-amber-800',
    pill: 'bg-amber-50 text-amber-800 ring-amber-200',
  },
  orange: {
    card: 'border-orange-200/80 bg-orange-50/70',
    solid: 'bg-orange-500',
    chip: 'bg-orange-100 text-orange-700',
    amount: 'text-orange-900',
    label: 'text-orange-800',
    pill: 'bg-orange-50 text-orange-800 ring-orange-200',
  },
  violet: {
    card: 'border-violet-200/80 bg-violet-50/70',
    solid: 'bg-violet-500',
    chip: 'bg-violet-100 text-violet-700',
    amount: 'text-violet-900',
    label: 'text-violet-800',
    pill: 'bg-violet-50 text-violet-800 ring-violet-200',
  },
  emerald: {
    card: 'border-emerald-200/80 bg-emerald-50/70',
    solid: 'bg-emerald-500',
    chip: 'bg-emerald-100 text-emerald-700',
    amount: 'text-emerald-900',
    label: 'text-emerald-800',
    pill: 'bg-emerald-50 text-emerald-800 ring-emerald-200',
  },
  teal: {
    card: 'border-teal-200/80 bg-teal-50/70',
    solid: 'bg-teal-600',
    chip: 'bg-teal-100 text-teal-700',
    amount: 'text-teal-900',
    label: 'text-teal-800',
    pill: 'bg-teal-50 text-teal-800 ring-teal-200',
  },
  indigo: {
    card: 'border-indigo-200/80 bg-indigo-50/70',
    solid: 'bg-indigo-500',
    chip: 'bg-indigo-100 text-indigo-700',
    amount: 'text-indigo-900',
    label: 'text-indigo-800',
    pill: 'bg-indigo-50 text-indigo-800 ring-indigo-200',
  },
  rose: {
    card: 'border-rose-200/80 bg-rose-50/70',
    solid: 'bg-rose-500',
    chip: 'bg-rose-100 text-rose-700',
    amount: 'text-rose-900',
    label: 'text-rose-800',
    pill: 'bg-rose-50 text-rose-800 ring-rose-200',
  },
  slate: {
    card: 'border-slate-200 bg-slate-50/70',
    solid: 'bg-slate-400',
    chip: 'bg-slate-100 text-slate-600',
    amount: 'text-slate-900',
    label: 'text-slate-700',
    pill: 'bg-slate-50 text-slate-700 ring-slate-200',
  },
};

/** Settlement stage → colour. */
export const SETTLEMENT_TONE: Record<string, StageTone> = {
  PENDING_REFUND_WINDOW: 'amber',
  PENDING_COD_REMITTANCE: 'orange',
  ELIGIBLE: 'violet',
  SETTLED: 'emerald',
  HELD: 'slate',
  REVERSED: 'rose',
};

/** Payout request status → colour. */
export const PAYOUT_TONE: Record<string, StageTone> = {
  PENDING: 'amber',
  APPROVED: 'violet',
  RELEASED: 'teal',
  REJECTED: 'rose',
  FAILED: 'rose',
};
